import React, { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { useSelector } from 'react-redux'; // Import useSelector to get user role
import {
    getMyObservations,
    getMyAssignedObservations,
    getObservations // Import getObservations for admin
} from '../services/observationAPI'; // Import existing API functions

import {
    AlertTriangle, // For unsafe observations/actions
    CheckCircle, // For completed actions or potentially safe observations (if added back)
    Clock, // For pending actions
    ClipboardList, // For observations
    Bell // General notification icon
} from 'lucide-react'; // Import icons

const NotificationsPage = () => {
    const { user } = useSelector((state) => state.profile); // Get user from Redux state
    const [notifications, setNotifications] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        const fetchNotifications = async () => {
            if (!user) {
                setNotifications([]);
                setLoading(false);
                return;
            }

            try {
                setLoading(true);
                setError(null); // Clear any previous errors

                let fetchedItems = [];

                if (user.accountType === 'Admin') {
                    // For Admin: Fetch all observations (system-wide activity)
                    const observationsRes = await getObservations();
                     // Assuming getObservations returns { observations: [...] }
                    fetchedItems = (observationsRes.observations || [])
                         .filter(item => item && typeof item === 'object')
                         .map(obs => ({ ...obs, notificationType: 'observation' })); // Tag as observation

                } else {
                    // For Employee: Fetch user's observations and assigned actions
                    const [myObservationsRes, myAssignedActionsRes] = await Promise.all([
                        getMyObservations(),
                        getMyAssignedObservations()
                    ]);

                    const myObservations = (myObservationsRes.observations || [])
                        .filter(item => item && typeof item === 'object')
                        .map(obs => ({ ...obs, notificationType: 'observation' })); // Tag as observation

                    const myAssignedActions = (myAssignedActionsRes.observations || []) // Assuming actions are also in 'observations' array
                        .filter(item => item && typeof item === 'object')
                        .map(action => ({ ...action, notificationType: 'action' })); // Tag as action

                    // Combine observations and actions for employees
                    fetchedItems = [...myObservations, ...myAssignedActions];
                }

                // Sort notifications by creation date (most recent first)
                fetchedItems.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));

                setNotifications(fetchedItems);

            } catch (err) {
                console.error('Error fetching data for notifications:', err);
                setError('Failed to load notifications.');
                toast.error('Failed to load notifications.');
                setNotifications([]); // Ensure notifications is an empty array on error
            } finally {
                setLoading(false);
            }
        };

        fetchNotifications();
    }, [user]); // Rerun effect if user changes

    // Helper function to format timestamp
    const formatTimestamp = (timestamp) => {
        if (!timestamp) return 'N/A';
        const date = new Date(timestamp);
        return date.toLocaleString(); // Format date and time
    };

    // Helper function to get icon based on notification type and status
    const getNotificationIcon = (notification) => {
        if (notification.notificationType === 'observation') {
            // Assuming 'unsafe-act' and 'unsafe-condition' are the types
            if (notification.type === 'unsafe-act' || notification.type === 'unsafe-condition') {
                return <AlertTriangle className="h-5 w-5 text-red-600 flex-shrink-0" />;
            }
            // Add other observation types if needed (e.g., 'safe')
            // else if (notification.type === 'safe') {
            //     return <CheckCircle className="h-5 w-5 text-green-600 flex-shrink-0" />;
            // }
            else {
                 return <ClipboardList className="h-5 w-5 text-blue-600 flex-shrink-0" />; // Default icon for observations
            }
        } else if (notification.notificationType === 'action') {
            // Icon based on action status
            if (notification.status === 'completed') {
                return <CheckCircle className="h-5 w-5 text-green-600 flex-shrink-0" />;
            } else if (notification.status === 'waiting' || notification.status === 'in-progress' || notification.status === 'submitted') {
                 return <Clock className="h-5 w-5 text-amber-600 flex-shrink-0" />; // Clock or AlertTriangle for pending
            }
            else {
                 return <ClipboardList className="h-5 w-5 text-gray-600 flex-shrink-0" />; // Default icon for actions
            }
        }
         return <Bell className="h-5 w-5 text-gray-500 flex-shrink-0" />; // Fallback icon

    };

     // Helper function to get notification message based on type and content
     const getNotificationMessage = (notification, userRole) => {
        if (notification.notificationType === 'observation') {
            const observationType = notification.type === 'unsafe-act' ? 'Unsafe Act' :
                                    notification.type === 'unsafe-condition' ? 'Unsafe Condition' :
                                    notification.type; // Fallback to raw type
            const areaName = notification.area?.name || 'Unknown Area';
            const descriptionSnippet = typeof notification.description === 'string' ? notification.description.substring(0, 50) + (notification.description.length > 50 ? '...' : '') : 'No description';

             if (userRole === 'Admin') {
                 // Admin sees observation from anyone
                 return `New ${observationType} Observation in ${areaName} by ${notification.observedBy?.firstName || 'Someone'}: "${descriptionSnippet}"`;
             } else {
                 // Employee sees their own observation
                 return `Your ${observationType} Observation in ${areaName}: "${descriptionSnippet}"`;
             }

        } else if (notification.notificationType === 'action') {
            const status = typeof notification.status === 'string' ? notification.status.replace('-', ' ').toLowerCase() : 'unknown status';
            const descriptionSnippet = typeof notification.description === 'string' ? notification.description.substring(0, 50) + (notification.description.length > 50 ? '...' : '') : 'No description';
            // Actions are typically assigned, so this message works for employees
            return `Action updated to ${status}: "${descriptionSnippet}"`;
        }
        return 'Unknown notification';
     };


    if (loading) {
        return (
            <div className="flex justify-center items-center h-64">
                <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="text-center text-red-600 mt-8">
                <p>{error}</p>
            </div>
        );
    }

    return (
        <div className="space-y-6 p-4">
            <div>
                <h1 className="text-2xl font-bold text-gray-900">Notifications</h1>
                <p className="text-gray-600">View your recent activity and updates.</p> {/* Updated description */}
            </div>

            <div className="bg-white shadow overflow-hidden sm:rounded-md">
                {notifications.length === 0 ? (
                    <div className="px-6 py-4 text-center text-gray-500">
                        {user?.accountType === 'Admin' ?
                            'No recent system activity to show.' :
                            'You have no recent notifications.'
                        }
                    </div>
                ) : (
                    <ul className="divide-y divide-gray-200">
                        {notifications.map((notification, index) => (
                            // Use a combination of notificationType and _id/id for a more unique key
                            <li key={`${notification.notificationType}-${notification._id || notification.id || index}`} className="px-6 py-4">
                                <div className="flex items-start space-x-4">
                                    {/* Display icon based on notification type/status */}
                                    <div className="flex-shrink-0 p-2 rounded-full bg-gray-100"> {/* Generic background */}
                                       {getNotificationIcon(notification)}
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <div className="flex items-center justify-between mb-1">
                                            {/* Display Notification Message */}
                                            <p className="text-sm font-medium text-gray-900">
                                                {getNotificationMessage(notification, user?.accountType)} {/* Pass user role */}
                                            </p>
                                            {/* Display Timestamp */}
                                            <p className="text-xs text-gray-500">
                                                {formatTimestamp(notification.createdAt || notification.timestamp)}
                                            </p>
                                        </div>
                                        {/* You can add more details here if needed, e.g., link to observation/action */}
                                        {/* Example: Link to the observation or action */}
                                        {/* {notification.notificationType === 'observation' && notification._id && (
                                            <Link to={`/employee/observations/${notification._id}`} className="text-sm text-blue-600 hover:underline mt-1 block">View Observation</Link>
                                        )}
                                        {notification.notificationType === 'action' && notification._id && (
                                             <Link to={`/employee/actions/${notification._id}`} className="text-sm text-blue-600 hover:underline mt-1 block">View Action</Link>
                                        )} */}
                                    </div>
                                </div>
                            </li>
                        ))}
                    </ul>
                )}
            </div>
        </div>
    );
};

export default NotificationsPage;
